import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NavbarItem from './NavbarItem';
import './_navbar.scss';
import api from '../../api/api';
import { useState, useEffect, useRef } from 'react';
import { errorHandler } from '../../utils/helper';
import { authActions } from '../../store/auth';
import { Col, Dropdown, Image, Menu, Modal, Row, Space } from 'antd';
import { UserCircle } from 'iconoir-react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import NotificationDrawer from '../NotificationDrawer/NotificationDrawer';
import { useCollapseNavbar } from '../../hooks/useCollapseNavbar';
// Import navbar icons
import dashboardIcon from '../../assets/icons/navbar/dashboard.svg';
import partnerManagerIcon from '../../assets/icons/navbar/partner-manager.svg';
import reportsIcon from '../../assets/icons/navbar/reports.svg';
import productsTabIcon from '../../assets/icons/navbar/products-tab.svg';
import offersIcon from '../../assets/icons/navbar/offers.svg';
import claimsManagerIcon from '../../assets/icons/navbar/claims-manager.svg';
import providersIcon from '../../assets/icons/navbar/providers.svg';
import tpaIcon from '../../assets/icons/navbar/tpa.svg';
import userManagementIcon from '../../assets/icons/navbar/user-management.svg';
import agentManagerIcon from '../../assets/icons/navbar/agent-manager.svg';
import collapseCloseIcon from '../../assets/icons/navbar/collapse-close.svg';
import collapseOpenIcon from '../../assets/icons/navbar/collapse-open.svg';
import knowledgeCenterIcon from '../../assets/icons/navbar/knowledge-center.svg';
import logoutIcon from '../../assets/icons/navbar/logout.svg';
import notificationIcon from '../../assets/icons/navbar/notifications.svg';
import preferencesIcon from '../../assets/icons/navbar/preferences.svg';
import settingsIcon from '../../assets/icons/settings.svg';
import { TextWithIcon } from '../UI/TextWithIcon/TextWithIcon';

const { confirm } = Modal;


const Navbar: React.FC = () => {
  const navigate = useNavigate(),
    brokerName = useSelector((state: any) => state.brokerconfig.brandName),
    controls = useSelector((state: any) => state.auth.controls),
    permissions = useSelector((state: any) => state.auth.permissions),
    dispatch = useDispatch(),
    name = useSelector((state: any) => state.auth.name),
    { favicon, logoUrl } = useSelector((state: any) => state.brokerconfig),
    [notificationDrawerVisible, setNotificationDrawerVisible] = useState<boolean>(false),
    [menuVisible, setMenuVisible] = useState(false),
    menuRef = useRef<HTMLDivElement>(null),
    { collapsed, hoverExpanded, toggleCollapse, handleMouseEnter, handleMouseLeave } = useCollapseNavbar(),

    navigateToKnowledgeCenter = () => {
      setMenuVisible(false);
      window.open(
        'https://learn.zohopublic.in/external/manual/partner/article/portal-handguide-final?p=ceeef6965bc937132fb23f08ade6a69639bcc7203aeba9fa5054c1f9cf669a91',
        '_blank'
      );
      setMenuVisible(false);
    },
    logoutHandler = () => {
      api.profile
        .logout()
        .then((res) => {
          dispatch(authActions.logout());
        })
        .catch((err) => {
          errorHandler(err?.data?.message ?? 'Something went wrong');
        });
    },
    logoutConfirmation = () => {
      setMenuVisible(false);
      confirm({
        title: 'Do you sure you want to logout?',
        icon: <ExclamationCircleFilled />,
        // content: 'Some descriptions',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          logoutHandler();
        },
        onCancel() { },
      });
    };


  // Handle clicking outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuVisible(false);
      }
    };

    if (menuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuVisible]);


  return (
    <div
      className={`navbar ${(collapsed && !hoverExpanded) ? 'collapsed' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NotificationDrawer visible={notificationDrawerVisible} onClose={() => {
        setNotificationDrawerVisible(false);
      }} />
      <div>
        <Row align="middle" justify="space-between" style={{ width: '100%', marginBottom: '24px' }}>
          <Col className='w-full'>
            {(collapsed && !hoverExpanded)
              ? <Image src={favicon} alt="favicon" className="navbar-favicon" preview={false} />
              : <Image src={logoUrl} alt="logo" className="navbar-logo" preview={false} />}
          </Col>
          {(!collapsed || hoverExpanded) && <Col className="collapse-icon">
            <img src={collapsed ? collapseOpenIcon : collapseCloseIcon} alt="collapse" onClick={toggleCollapse} />
          </Col>}
        </Row>

        <Space direction="vertical" className="navbar-space">
          {permissions?.dashboard?.includes('view') && !controls?.is_admin && <NavbarItem to="/dashboard" icon={dashboardIcon}>Dashboard</NavbarItem>}
          {permissions?.client?.includes('view') && !controls?.is_admin && <NavbarItem to="/client-manager" icon={partnerManagerIcon}>Client Manager</NavbarItem>}

          {permissions?.user_management?.includes('view') && controls?.is_admin && (
            <NavbarItem to="/agent-manager" icon={agentManagerIcon}>Agent Manager</NavbarItem>
          )}
          {permissions?.products?.includes('view') && !controls?.is_admin && (
            <NavbarItem to="/reports" icon={reportsIcon}>Reports</NavbarItem>
          )}
          {permissions?.products?.includes('view') && <NavbarItem to="/products" icon={productsTabIcon}>Products</NavbarItem>}
          {/* {permissions?.products?.includes('view') && controls?.navnar_options?.offers_and_plans && (
            <NavbarItem to="/offers-and-plans" icon={offersIcon}>Offers and Plans</NavbarItem>
          )} */}
          {permissions?.partners?.includes('view') && controls?.is_partner_section && (
            <NavbarItem to="/offers" icon={offersIcon}>Offers</NavbarItem>
          )}
          {permissions?.claims?.includes('view') && !controls?.is_admin && (
            <NavbarItem to="/claims-manager" icon={claimsManagerIcon}>Claims Manager</NavbarItem>
          )}
          {permissions?.providers?.includes('view') && controls?.is_admin && (
            <NavbarItem to="/providers" icon={providersIcon}>Providers</NavbarItem>
          )}
          {permissions?.tpa_master?.includes('view') && controls?.is_admin && (
            <NavbarItem to="/tpa-master" icon={tpaIcon}>TPA Master</NavbarItem>
          )}
          {permissions?.partners?.includes('view') && controls?.is_partner_section && (
            <NavbarItem to="/orp-partners" icon={partnerManagerIcon}>ORP Partners</NavbarItem>
          )}
          {/* {permissions?.user_management?.includes('view') && (
            <NavbarItem to="/user-management" icon={userManagementIcon}>User Management</NavbarItem>
          )} */}
          <NavbarItem to="/settings" icon={settingsIcon}>Settings</NavbarItem>

          {/* {brokerName?.toLowerCase() === 'bharatsure' && <NavbarItem to="/send-ecard" icon={ecardIcon}>Send Ecard</NavbarItem>} */}
        </Space>
      </div>

      <Space direction="vertical" className="navbar-space">
        <NavbarItem icon={knowledgeCenterIcon} onClick={navigateToKnowledgeCenter}>Knowledge Base</NavbarItem>
        {!controls?.is_admin && <NavbarItem onClick={() => {
          setNotificationDrawerVisible(true);
        }} icon={notificationIcon}>Notifications</NavbarItem>}
        <div className="navbar-user-menu" ref={menuRef}>
          <NavbarItem
            icon={<UserCircle />}
            group={true}
            onClick={() => setMenuVisible(!menuVisible)}
          >
            {name}
          </NavbarItem>
          {menuVisible && (
            <div className="navbar-menu-overlay">
              <Menu
                mode="vertical"
                className="navbar-menu"
              >
                {/* ...!controls?.is_admin && (
                  <Menu.Item key="configuration">
                    <div className="navbar-menu-item" onClick={() => {
                      setMenuVisible(false);
                      navigate('/configuration');
                    }}>
                      <img src={preferencesIcon} alt="preferences" />
                      <span>Configuration</span>
                    </div>
                  </Menu.Item>
                ) */}
                <Menu.Item key="logout">
                  <div className="navbar-menu-item" onClick={logoutConfirmation}>
                    <img src={logoutIcon} alt="logout" />
                    <span>Logout</span>
                  </div>
                </Menu.Item>
              </Menu>
              <div className="navbar-menu-footer">
              ISO/IEC 27001:2022 Certified
              </div>
            </div>
          )}
        </div>
      </Space>
    </div>
  );
};

export default Navbar;
